/**
 * Tooltip Manager - Handles floating tooltips
 *
 * Singleton manager for floating tooltips.
 * Only one tooltip visible at a time.
 * Desktop: Show on hover
 * Mobile: Show on click/tap
 *
 * @package PresszoneComments
 */

/**
 * TooltipManager class
 * Manages a single floating tooltip element
 */
class TooltipManager {
    constructor() {
        this.activeTooltip = null;
        this.tooltipElement = null;
        this.isMobile = ('ontouchstart' in window);
        
        // Close tooltip when clicking outside
        document.addEventListener('click', (e) => {
            if (this.activeTooltip && !e.target.closest('.presszone-comments-tooltip-trigger')) {
                this.hide();
            }
        });
        
        // Reposition tooltip on window resize
        window.addEventListener('resize', () => {
            if (this.activeTooltip && this.tooltipElement && this.tooltipElement.style.display === 'block') {
                this.position(this.activeTooltip);
            }
        });
        
        // Reposition tooltip on scroll
        window.addEventListener('scroll', () => {
            if (this.activeTooltip && this.tooltipElement && this.tooltipElement.style.display === 'block') {
                this.position(this.activeTooltip);
            }
        }, { passive: true });
    }
    
    /**
     * Show tooltip near trigger element
     *
     * @param {HTMLElement} triggerElement - The element that triggered the tooltip
     * @param {string} content - Tooltip text content
     */
    show(triggerElement, content) {
        // Close existing tooltip
        this.hide();
        
        // Create tooltip element if needed
        if (!this.tooltipElement) {
            this.tooltipElement = document.createElement('div');
            this.tooltipElement.className = 'presszone-comments-floating-tooltip';
            this.tooltipElement.setAttribute('role', 'tooltip');
            document.body.appendChild(this.tooltipElement);
        }
        
        // Set content
        this.tooltipElement.textContent = content;
        this.tooltipElement.style.display = 'block';
        
        // Position near trigger
        this.position(triggerElement);
        
        this.activeTooltip = triggerElement;
    }
    
    /**
     * Position tooltip near trigger element
     * Smart positioning: above by default, below if no space above
     *
     * @param {HTMLElement} triggerElement - The element to position near
     */
    position(triggerElement) {
        const triggerRect = triggerElement.getBoundingClientRect();
        const tooltip = this.tooltipElement;
        
        // Get tooltip dimensions (force layout)
        tooltip.style.visibility = 'hidden';
        tooltip.style.display = 'block';
        const tooltipWidth = tooltip.offsetWidth;
        const tooltipHeight = tooltip.offsetHeight;
        tooltip.style.visibility = '';
        
        // Position above trigger by default
        let top = triggerRect.top - tooltipHeight - 8;
        let left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2);
        
        // If tooltip goes off top of screen, show below
        if (top < 10) {
            top = triggerRect.bottom + 8;
            tooltip.classList.add('presszone-comments-floating-tooltip--below');
        } else {
            tooltip.classList.remove('presszone-comments-floating-tooltip--below');
        }
        
        // If tooltip goes off left edge
        if (left < 10) {
            left = 10;
        }
        
        // If tooltip goes off right edge
        if (left + tooltipWidth > window.innerWidth - 10) {
            left = window.innerWidth - tooltipWidth - 10;
        }
        
        tooltip.style.top = `${top + window.scrollY}px`;
        tooltip.style.left = `${left}px`;
    }
    
    /**
     * Hide active tooltip
     */
    hide() {
        if (this.tooltipElement) {
            this.tooltipElement.style.display = 'none';
            this.tooltipElement.classList.remove('presszone-comments-floating-tooltip--below');
        }
        this.activeTooltip = null;
    }
}

// Singleton instance
const tooltipManager = new TooltipManager();

export default tooltipManager;
